import React, { useState, useEffect } from 'react';
import { Check, Timer, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { Heading } from '../../typography/Heading';
import { Text } from '../../typography/Text';
import { Plan, startCheckoutSession } from '../Pricing';
import { onSnapshot } from 'firebase/firestore';

export function PricingCard({
  title,
  description,
  price,
  duration,
  features,
  isLimitedOffer,
  priceId,
}: Plan) {
  const [loading, setLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    function calculateTimeUntilEndOfDay() {
      const now = new Date();
      const endOfDay = new Date(now);
      endOfDay.setHours(23, 59, 59, 999);
      return Math.floor((endOfDay.getTime() - now.getTime()) / 1000);
    }

    setTimeLeft(calculateTimeUntilEndOfDay());

    const timer = setInterval(() => {
      const remaining = calculateTimeUntilEndOfDay();
      setTimeLeft(remaining);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPrice = (price: string) => {
    const numPrice = parseFloat(price);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numPrice);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.3 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`relative bg-white rounded-2xl p-10 flex flex-col justify-between min-h-[320px] ${
        isLimitedOffer
          ? 'shadow-[0_8px_30px_rgb(138,99,210,0.15)]'
          : 'shadow-lg'
      } overflow-visible mt-8`}
    >
      {/* Background gradient effect */}
      <div 
        className={`absolute inset-0 bg-gradient-to-br transition-opacity duration-500 ${
          isLimitedOffer
            ? 'from-primary/5 via-purple-500/5 to-purple-500/5'
            : 'from-gray-50 to-white'
        } ${isHovered ? 'opacity-100' : 'opacity-50'}`}
      />

      {/* Popular badge with timer */}
      {isLimitedOffer && (
        <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 z-10 w-full max-w-[280px]">
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="relative"
          >
            {/* Main badge */}
            <div className="bg-purple-gradient text-white px-6 py-3 rounded-t-xl shadow-lg text-center">
              <div className="flex items-center justify-center space-x-2 mb-1">
                <Sparkles className="w-4 h-4" />
                <span className="font-medium">Most Popular</span>
              </div>
              <div className="flex items-center justify-center text-xs space-x-1.5 text-white/90">
                <Timer className="w-3.5 h-3.5" />
                <span>Offer ends in: {formatTime(timeLeft)}</span>
              </div>
            </div>
            {/* Decorative bottom corners */}
            <div className="absolute -bottom-2 left-0 w-2 h-2 bg-primary-hover" style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }} />
            <div className="absolute -bottom-2 right-0 w-2 h-2 bg-purple-700" style={{ clipPath: 'polygon(0 0, 0 100%, 100% 0)' }} />
          </motion.div>
        </div>
      )}

      <div className="relative flex-1 flex flex-col">
        {/* Header */}
        <div className="text-center mb-10">
          <Heading level={3} align="center" className="mb-3 text-2xl">
            {title}
          </Heading>
          <Text align="center" className="text-slate-light mb-8">
            {description}
          </Text>
          <div className="flex items-baseline justify-center">
            <Text 
              size="xl" 
              className={`text-4xl font-bold ${isLimitedOffer ? 'text-primary' : 'text-slate'}`}
            >
              {formatPrice(price)}
            </Text>
            <Text className="text-slate-light ml-2">/ {duration}</Text>
          </div>
        </div>

        {/* Features list */}
        <ul className="space-y-5 mb-10 flex-1">
          {features.map((feature, index) => (
            <motion.li 
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`flex items-start text-slate ${
                index === features.length - 1 
                  ? 'bg-gradient-to-r from-primary/10 to-purple-500/10 p-4 rounded-xl border border-primary/10 relative' 
                  : ''
              }`}
            >
              <div className={`flex-shrink-0 rounded-full p-1 mt-1 ${
                index === features.length - 1 
                  ? 'bg-primary/10' 
                  : 'bg-success/10'
              }`}>
                <Check className={`h-4 w-4 ${
                  index === features.length - 1 
                    ? 'text-primary' 
                    : 'text-success'
                }`} />
              </div>
              <Text className={`ml-3 ${index === features.length - 1 ? 'font-medium' : ''}`}>
                {feature}
              </Text>
              {index === features.length - 1 && (
                <div className="absolute -top-3 -right-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg animate-pulse">
                  NEW
                </div>
              )}
            </motion.li>
          ))}
        </ul>
      </div>

      {/* CTA Button всегда внизу */}
      <div className="mt-6">
        <button
          className={`w-full py-4 px-6 rounded-xl text-white shadow-lg transition-all duration-300 ${
            isLimitedOffer
              ? 'bg-purple-gradient hover:bg-purple-gradient-hover'
              : 'bg-slate hover:bg-slate-light'
          } ${loading ? 'opacity-75 cursor-not-allowed' : 'transform hover:-translate-y-1'}`}
          disabled={loading}
          onClick={() => {
            // Определяем режим оплаты по наличию recurring
            const isOneTime = !duration || duration === 'one-time';
            const mode: 'payment' | 'subscription' = isOneTime ? 'payment' : 'subscription';
            startCheckoutSession(
              priceId,
              mode,
              () => {
                setLoading(true);
              },
              (docRef) => {
                onSnapshot(docRef, (snap) => {
                  const { error, url } = snap.data() as any;
                  if (error) {
                    alert(`An error occurred: ${error.message}`);
                    setLoading(false);
                  }
                  if (url) {
                    window.location.assign(url);
                    setLoading(false);
                  }
                });
              },
              (errorArg) => {
                setLoading(false);
                console.log(errorArg);
              }
            );
          }}
        >
          <Text className="font-medium">
            {loading ? 'Processing...' : 'Get Started'}
          </Text>
        </button>
      </div>
    </motion.div>
  );
}